import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useForm } from 'react-hook-form';
import { Phone, Mail, MapPin, Clock, Send, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactoPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <CustomCursor />
      <Navbar />
      
      {/* Hero Section */}
      <HeroSection />
      
      {/* Contact Form Section */}
      <ContactFormSection />
      
      {/* Map Section */}
      <MapSection />
      
      <Footer />
    </div>
  );
};

const HeroSection = () => {
  const { t } = useLanguage();
  
  return (
    <section className="relative pt-32 pb-20 bg-gradient-to-b from-[#0A0A0A] to-[#0F0F0F]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
        >
          {t('contactPage.heroTagline')}
        </motion.span>
        
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-montserrat font-light text-4xl md:text-6xl text-white tracking-[0.05em] uppercase mb-6"
        >
          {t('contactPage.heroTitle1')}
          <br />
          <span className="text-[#C5A55C]">{t('contactPage.heroTitle2')}</span>
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-raleway font-light text-lg text-white/60 max-w-2xl mx-auto"
        >
          {t('contactPage.heroDescription')}
        </motion.p>
      </div>
    </section>
  );
};

const ContactFormSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const { t, language } = useLanguage();
  
  const serviceOptions = t('contactPage.serviceOptions');
  const budgetOptions = t('contactPage.budgetOptions');
  const timelineOptions = t('contactPage.timelineOptions');
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          files: uploadedFiles.map(f => f.name),
        }),
      });
      
      if (response.ok) {
        setIsSuccess(true);
        toast.success(language === 'es' ? 'Solicitud de consulta enviada exitosamente!' : 'Consultation request submitted successfully!');
        reset();
        setUploadedFiles([]);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Algo salió mal. Por favor intente de nuevo.' : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles(prev => [...prev, ...files]);
  };
  
  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  return (
    <section ref={ref} className="py-20 bg-gradient-to-b from-[#F8F6F1] to-[#EFECE5] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      <div className="absolute top-8 left-8 w-20 h-20 border-l-2 border-t-2 border-[#C5A55C]/30" />
      <div className="absolute top-8 right-8 w-20 h-20 border-r-2 border-t-2 border-[#C5A55C]/30" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Form - 3 columns */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1 }}
            className="lg:col-span-3"
          >
            {isSuccess ? (
              <SuccessMessage t={t} />
            ) : (
              <div className="bg-white p-8 md:p-12 shadow-2xl">
                <h2 className="font-montserrat font-light text-3xl text-[#1A1A1A] tracking-[0.05em] uppercase mb-2">
                  {t('contactPage.formTitle')}
                </h2>
                <div className="w-16 h-[2px] bg-[#C5A55C] mb-8" />
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form-full">
                  {/* Row 1: Name & Phone */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                        {t('contactPage.formLabels.name')} *
                      </label>
                      <input
                        {...register('name', { required: language === 'es' ? 'Nombre es requerido' : 'Name is required' })}
                        type="text"
                        placeholder={t('contactPage.formPlaceholders.name')}
                        className="w-full bg-[#F8F6F1] border-2 border-transparent focus:border-[#C5A55C] py-4 px-5 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none transition-colors duration-300 font-raleway"
                        data-testid="input-name"
                      />
                      {errors.name && <span className="text-red-500 text-xs mt-1">{errors.name.message}</span>}
                    </div>
                    
                    <div>
                      <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                        {t('contactPage.formLabels.phone')} *
                      </label>
                      <input
                        {...register('phone', { required: language === 'es' ? 'Teléfono es requerido' : 'Phone is required' })}
                        type="tel"
                        placeholder={t('contactPage.formPlaceholders.phone')}
                        className="w-full bg-[#F8F6F1] border-2 border-transparent focus:border-[#C5A55C] py-4 px-5 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none transition-colors duration-300 font-raleway"
                        data-testid="input-phone"
                      />
                      {errors.phone && <span className="text-red-500 text-xs mt-1">{errors.phone.message}</span>}
                    </div>
                  </div>
                  
                  {/* Row 2: Email & Address */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                        {t('contactPage.formLabels.email')} *
                      </label>
                      <input
                        {...register('email', { 
                          required: language === 'es' ? 'Email es requerido' : 'Email is required',
                          pattern: { value: /^\S+@\S+$/i, message: language === 'es' ? 'Email inválido' : 'Invalid email' }
                        })}
                        type="email"
                        placeholder={t('contactPage.formPlaceholders.email')}
                        className="w-full bg-[#F8F6F1] border-2 border-transparent focus:border-[#C5A55C] py-4 px-5 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none transition-colors duration-300 font-raleway"
                        data-testid="input-email"
                      />
                      {errors.email && <span className="text-red-500 text-xs mt-1">{errors.email.message}</span>}
                    </div>
                    
                    <div>
                      <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                        {t('contactPage.formLabels.address')}
                      </label>
                      <input
                        {...register('address')}
                        type="text"
                        placeholder={t('contactPage.formPlaceholders.address')}
                        className="w-full bg-[#F8F6F1] border-2 border-transparent focus:border-[#C5A55C] py-4 px-5 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none transition-colors duration-300 font-raleway"
                        data-testid="input-address"
                      />
                    </div>
                  </div>
                  
                  {/* Row 3: Service Type */}
                  <div>
                    <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                      {t('contactPage.formLabels.serviceType')} *
                    </label>
                    <select
                      {...register('serviceType', { required: language === 'es' ? 'Por favor seleccione un servicio' : 'Please select a service' })}
                      className="w-full bg-[#F8F6F1] border-2 border-transparent focus:border-[#C5A55C] py-4 px-5 text-[#1A1A1A] focus:outline-none transition-colors duration-300 font-raleway appearance-none cursor-pointer"
                      data-testid="select-service"
                    >
                      <option value="">{t('contactPage.formPlaceholders.serviceType')}</option>
                      {serviceOptions.map((option, i) => (
                        <option key={i} value={option}>{option}</option>
                      ))}
                    </select>
                    {errors.serviceType && <span className="text-red-500 text-xs mt-1">{errors.serviceType.message}</span>}
                  </div>
                  
                  {/* Row 4: Budget & Timeline */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                        {t('contactPage.formLabels.budget')}
                      </label>
                      <select
                        {...register('budget')}
                        className="w-full bg-[#F8F6F1] border-2 border-transparent focus:border-[#C5A55C] py-4 px-5 text-[#1A1A1A] focus:outline-none transition-colors duration-300 font-raleway appearance-none cursor-pointer"
                        data-testid="select-budget"
                      >
                        <option value="">{t('contactPage.formPlaceholders.budget')}</option>
                        {budgetOptions.map((option, i) => (
                          <option key={i} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                        {t('contactPage.formLabels.timeline')}
                      </label>
                      <select
                        {...register('timeline')}
                        className="w-full bg-[#F8F6F1] border-2 border-transparent focus:border-[#C5A55C] py-4 px-5 text-[#1A1A1A] focus:outline-none transition-colors duration-300 font-raleway appearance-none cursor-pointer"
                        data-testid="select-timeline"
                      >
                        <option value="">{t('contactPage.formPlaceholders.timeline')}</option>
                        {timelineOptions.map((option, i) => (
                          <option key={i} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Row 5: Project Description */}
                  <div>
                    <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                      {t('contactPage.formLabels.message')}
                    </label>
                    <textarea
                      {...register('message')}
                      placeholder={t('contactPage.formPlaceholders.message')}
                      rows={5}
                      className="w-full bg-[#F8F6F1] border-2 border-transparent focus:border-[#C5A55C] py-4 px-5 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none transition-colors duration-300 font-raleway resize-none"
                      data-testid="input-message"
                    />
                  </div>
                  
                  {/* Row 6: File Upload */}
                  <div>
                    <label className="font-montserrat font-medium text-xs tracking-[0.15em] text-[#1A1A1A]/60 uppercase block mb-2">
                      {t('contactPage.formLabels.files')}
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                        data-testid="input-files"
                      />
                      <label
                        htmlFor="file-upload"
                        className="flex items-center justify-center gap-3 w-full bg-[#F8F6F1] border-2 border-dashed border-[#C5A55C]/30 hover:border-[#C5A55C] py-6 px-5 cursor-pointer transition-colors duration-300"
                      >
                        <Upload className="w-5 h-5 text-[#C5A55C]" />
                        <span className="font-raleway text-[#1A1A1A]/60">{t('contactPage.formPlaceholders.files')}</span>
                      </label>
                    </div>
                    
                    {/* Uploaded files list */}
                    {uploadedFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-[#F8F6F1] px-4 py-2">
                            <span className="font-raleway text-sm text-[#1A1A1A]/70">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-red-500 text-sm hover:text-red-700"
                            >
                              {t('contactPage.remove')}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full mt-4 px-12 py-5 bg-[#C5A55C] text-white hover:bg-[#1A1A1A] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-4"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? t('contactPage.submitting') : t('contactPage.submitButton')}
                    <Send className="w-4 h-4" />
                  </motion.button>
                </form>
              </div>
            )}
          </motion.div>
          
          {/* Contact Info - 2 columns */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-[#1A1A1A] p-8 md:p-10 h-full relative">
              {/* Gold corner accents */}
              <div className="absolute top-0 left-0 w-12 h-12 border-l-4 border-t-4 border-[#C5A55C] -translate-x-2 -translate-y-2" />
              
              <h3 className="font-montserrat font-light text-2xl text-white tracking-[0.05em] uppercase mb-2">
                {t('contactPage.contactInfoTitle')}
              </h3>
              <div className="w-12 h-[2px] bg-[#C5A55C] mb-8" />
              
              <div className="space-y-8">
                <ContactInfoItem
                  icon={Phone}
                  title={t('contactPage.phone')}
                  content="(215) 555-0123"
                  link="tel:+12155550123"
                />
                <ContactInfoItem
                  icon={Mail}
                  title={t('contactPage.email')}
                  content="info@martinez-solutions.com"
                  link="mailto:info@martinez-solutions.com"
                />
                <ContactInfoItem
                  icon={MapPin}
                  title={t('contactPage.serviceArea')}
                  content={t('contactPage.serviceAreaValue')}
                />
              </div>
              
              {/* Business Hours */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <div className="flex items-center gap-3 mb-6">
                  <Clock className="w-5 h-5 text-[#C5A55C]" />
                  <span className="font-montserrat font-medium text-xs tracking-[0.2em] text-[#C5A55C] uppercase">
                    {t('contactPage.businessHours')}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-raleway text-white/60">{t('contactPage.weekdays')}</span>
                    <span className="font-raleway text-white">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-raleway text-white/60">{t('contactPage.saturday')}</span>
                    <span className="font-raleway text-white">9:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-raleway text-white/60">{t('contactPage.sunday')}</span>
                    <span className="font-raleway text-white/40">{t('contactPage.closed')}</span>
                  </div>
                </div>
              </div>
              
              {/* Logo */}
              <div className="mt-12">
                <img
                  src="https://customer-assets.emergentagent.com/job_734cb631-c213-4223-9ec5-3c4d6675302f/artifacts/573hpmih_Transparent-PNG.png"
                  alt="Martinez Solutions"
                  className="h-16 opacity-40"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="absolute bottom-8 left-8 w-20 h-20 border-l-2 border-b-2 border-[#C5A55C]/30" />
      <div className="absolute bottom-8 right-8 w-20 h-20 border-r-2 border-b-2 border-[#C5A55C]/30" />
    </section>
  );
};

const ContactInfoItem = ({ icon: Icon, title, content, link }) => (
  <div className="flex items-start gap-4">
    <div className="w-12 h-12 bg-[#C5A55C]/20 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-[#C5A55C]" />
    </div>
    <div>
      <span className="font-montserrat font-medium text-[10px] tracking-[0.2em] text-white/40 uppercase block mb-1">
        {title}
      </span>
      {link ? (
        <a
          href={link}
          className="font-raleway text-lg text-white hover:text-[#C5A55C] transition-colors duration-300"
        >
          {content}
        </a>
      ) : (
        <span className="font-raleway text-lg text-white">{content}</span>
      )}
    </div>
  </div>
);

const SuccessMessage = ({ t }) => (
  <div className="bg-white p-12 shadow-2xl text-center">
    <div className="w-20 h-20 mx-auto mb-8 bg-[#C5A55C]/20 flex items-center justify-center">
      <CheckCircle className="w-10 h-10 text-[#C5A55C]" />
    </div>
    <h2 className="font-montserrat font-light text-3xl text-[#1A1A1A] tracking-[0.05em] uppercase mb-4">
      {t('contactPage.successTitle')}
    </h2>
    <p className="font-raleway text-[#1A1A1A]/60 max-w-md mx-auto mb-8">
      {t('contactPage.successMessage')}
    </p>
    <div className="w-16 h-[2px] bg-[#C5A55C] mx-auto" />
  </div>
);

const MapSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  
  return (
    <section ref={ref} className="bg-[#1A1A1A]">
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 1 }}
      >
        {/* Map Header */}
        <div className="py-12 text-center">
          <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase">
            {t('contactPage.mapTitle')}
          </span>
        </div>
        
        {/* Google Maps Embed */}
        <div className="h-[400px] w-full">
          <iframe
            title="Martinez Solutions Service Area"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d195601.5765138372!2d-75.28037863730319!3d39.99752401779408!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6b7d8d4b54beb%3A0x89f514d88c3e58c1!2sPhiladelphia%2C%20PA!5e0!3m2!1sen!2sus!4v1707000000000!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'grayscale(100%) contrast(1.1)' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default ContactoPage;
